import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import './inputUpload.css';
import useTranslator from '../../../translation/hooks/useTranslator';
import { bytesToSize, CompressorImage } from '../../../utils'
import { useFormInfo, useFormInfoDispatch } from '../../../context/states/formInfo';
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

export const InputUpload = props => {
  const {
    name,
    accept,
    setValue,
    field,
    index,
    handleFilePost,
    handleFileDelete,
  } = props;

  const inputProps = { name, accept };
  const formInfoDispatch = useFormInfoDispatch();
  const [formInfo] = useFormInfo();
  const [fileTooBig, setFileTooBig] = useState([]);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const { Translation } = useTranslator();
  const useAttachmentV2 = formInfo.useAttachmentV2 && field.Field.TypeId === 19;
  const maxSizeBytes = useMemo(() => (useAttachmentV2 ? 26214400 : 5242880), [useAttachmentV2]);
  const maxSize = useMemo(() => bytesToSize(maxSizeBytes), [maxSizeBytes]);

  const uploadDisabled = useMemo(
    () => isLoading || formInfo.isFormDisabled,
    [formInfo.isFormDisabled, isLoading]
  );
  
  useEffect(() => {
    let attachmentObject = [];

    if (useAttachmentV2) {
      attachmentObject = files.map((file) => file.value);
    } else {
      attachmentObject = files.map((file) => ({
        Attachment: {
          Content: file.value,
          ContentType: file.type,
          FileName: file.label,
        },
      }));
    }

    if (attachmentObject.length === 1) {
      attachmentObject = attachmentObject[0];
    } else if (attachmentObject.length === 0) {
      attachmentObject = '';
    }

    setValue(attachmentObject, index, field.ObjectKey, field.Required);
  }, [files]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    })
  }

  const getFileTypeFromName = (file) => {
    let fileType = '';
    const fileExtension = file.name.split('.').at(-1);
    if (fileExtension === 'md') fileType = 'text/markdown';
    return fileType;
  }

  const postFile = async (file) => {
    const formData = new FormData();
    formData.append('Content', file);

    const response = await handleFilePost(formData);
    return response?.[0]?.Id;
  }

  const compressImage = async (file) => {
    if (!file || !CompressorImage.isValidImageType(file)) return file;
  
    const compressor = new CompressorImage(file, { format: file.type });
    const data = await compressor.compress();
  
    const response = await fetch(data.file);
    const blob = await response.blob();
  
    const blobFile = new File([blob], file.name, { type: blob.type });
    return blobFile;
  };

  const onChange = useCallback(async (e) => {
    if (useAttachmentV2) {
      formInfoDispatch({...formInfo, isSendDisabled: true});
      setIsLoading(true);
    }
    const isMultiple = field?.Field?.Multiple;
    const readFiles = [...e.target.files];
    const newFilesArray = [];
    const fileTooBigArray = [];

    await Promise.all(readFiles.map(async (file) => {
      if (file.size <= maxSizeBytes) {
        const compressedFile = await compressImage(file, { format: file.type });

        if (!useAttachmentV2) {
          return convertToBase64(compressedFile)
            .then(resolve => {
              const fileValueFromResolve = resolve.split(',').at(-1);
              newFilesArray.push({
                value: fileValueFromResolve,
                label: compressedFile.name,
                type: compressedFile.type || getFileTypeFromName(compressedFile),
              });
              const filesToUpload = isMultiple ? [...files, ...newFilesArray] : [...newFilesArray];
              setFiles(filesToUpload);
              e.target.value = null;
            });
        } else {
          const uploadedFiles = [...files];
          if (!isMultiple && files.length === 1) {
            try {
              await handleFileDelete(files[0].value);
              uploadedFiles.pop();
            } catch (e) {
              console.error(e);
              return;
            }
          }

          return postFile(compressedFile)
            .then((id) => {
              newFilesArray.push({
                value: id,
                label: compressedFile.name,
                type: compressedFile.type || getFileTypeFromName(compressedFile),
              });
              uploadedFiles.push(...newFilesArray);
              setFiles(uploadedFiles);
              e.target.value = null;
            })
            .catch((e) => console.error(e))
        }
      } else {
        fileTooBigArray.push({ id: -1, name: file.name });
      }
    }));

    if (useAttachmentV2) {
      formInfoDispatch({...formInfo, isSendDisabled: false});
      setIsLoading(false);
    }
    setFileTooBig(fileTooBigArray);
  }, [files, field, useAttachmentV2, maxSizeBytes]);

  const handleSelectChange = async (object, change) => {
    if ((change.action !== 'remove-value' && change.action !== 'clear') || !(useAttachmentV2)) {
      setFiles(object ? [...object] : []);
      return;
    }

    if (useAttachmentV2) {
      formInfoDispatch({...formInfo, isSendDisabled: true});
      setIsLoading(true);
    }
    const removedFiles = change.removedValue ? [change.removedValue] : change.removedValues;
    let updatedFiles = files;

    await Promise.all(
      removedFiles.map(file =>
        handleFileDelete(file.value)
          .then(() => {
            updatedFiles = updatedFiles.filter(f => f.value !== file.value);
          })
          .catch((e) => console.error(e))
      )
    );

    if (useAttachmentV2) {
      formInfoDispatch({...formInfo, isSendDisabled: false});
      setIsLoading(false);
    }
    setFiles([...updatedFiles]);
  }

  return (
    <div className='form-content__upload__wrapper'>
      {fileTooBig.length > 0 && 
        fileTooBig.map((item) => (
          <div key={item.id} className='form-content__validation'>{Translation('FIELDS.FILE_TOO_BIG_WARN', { item: item.name, size: maxSize.formated })}</div>
        ))
      }
      <div className='form-content__desc'>{Translation('FIELDS.FILE_MAX_SIZE_ALLOWED', { size: maxSize.formated })}</div>
      <div className='form-content__upload__inputs'>
        {isLoading && <div className='select-skeleton'>
          <Skeleton count={1} height={38} />
        </div>}
        <div className='form-content__select-wrapper'>
          {!isLoading && <Select
            placeholder=''
            isClearable={true}
            classNamePrefix='react-select'
            className='react-select-select'
            options={files}
            value={files}
            onChange={handleSelectChange}
            noOptionsMessage={() => Translation('FIELDS.INPUT_WITHOUT_OPTIONS')}
            isMulti={field.Field.Multiple}
            isSearchable={false}
            isDisabled={uploadDisabled}
            menuIsOpen={false}
          />}
        </div>
        <label htmlFor={field.Key} className={`form-content__upload__label ${uploadDisabled ? '--disabled' : ''}`} >
          <input 
            ref={inputRef}
            id={field.Key}
            className={`form-content__upload ${files.length > 0 ? '--has-file' : ''}`}
            onChange={onChange}
            type="file"
            disabled={uploadDisabled}
            {...inputProps}
            multiple={field.Field.Multiple}
          />
          {Translation('FIELDS.CHOOSE_FILE')}
        </label>
      </div>
    </div>
  )

}

InputUpload.propTypes = {
  name: PropTypes.string.isRequired,
  accept: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
}